import { ContentScore } from "./contentscore.model";
import { Description } from "./description.model";
import { DimensionDTO } from "./dimension.model";
import { KeyValueDTO } from "./keyvalue.model";
import { Media } from "./media.model";
import { PackageDTO } from "./package.model";
import { SizeChart, SizeChartTypes } from "./sizeChart.model";
import { SubProduct } from "./subproduct.model";
import { UnitDTO } from "./unit.model";
import { Variants } from "./variant.model";
import { WarrantyDTO } from "./warranty.model";

export class AddProductDTO {
    nameEn: string;
    nameUr: string;
    categoryId: string;
    productImages: Media[];
    promotionImage: Media | undefined;
    promotionWhiteImageBg: Media | undefined;
    productVideo: Media | undefined;
    description: Description;
    warranty: WarrantyDTO;
    containDangerousGoods: boolean;
    isDeliveryByBuyPass: boolean;
    package: PackageDTO;
    sellerBrandId: string;
    productSpecifications: KeyValueDTO[];
    variants: Variants[];
    subProduct: SubProduct[];
    contentScore: ContentScore;
    sizeChart: SizeChart | undefined;
    draftId: string = "";

    constructor(
        nameEn: string = "",
        nameUr: string = "",
        categoryId: string = "",
        productImages: Media[] = [],
        promotionImage: Media | undefined = undefined,
        promotionWhiteImageBg: Media | undefined = undefined,
        productVideo: Media | undefined = undefined,
        sizeChartImage: Media | undefined = undefined,
        productSpecifications: any = {},
        mainDescription: string = "",
        productDescription: string = "",
        highlights: string = "",
        whatIsInTheBox: string = "",
        warrantyType: string = "",
        warrantyPeriod: string = "",
        warrantyPolicy: string = "",
        containDangerousGoods: string = "None",
        variants: Variants[] = [],
        isIndividual: boolean = false,
        packageWeight: string = "",
        packageWeightUnit: any = '',
        packageLength: string = "",
        packageWidth: string = "",
        packageHeight: string = "",
        subProducts: any[] | SubProduct[] = [],
        draftId: string,
        sellerBrandId: string = "",
        contentScore: ContentScore = new ContentScore(),
        isDeliveryByBuyPass: boolean = true,
    ) {
        for (let variant of variants) {
            delete variant.options;
            delete variant.allowImages;
        }
        this.nameEn = nameEn;
        this.nameUr = nameUr;
        this.categoryId = categoryId;
        this.productImages = productImages;
        this.promotionImage = promotionImage;
        this.promotionWhiteImageBg = promotionWhiteImageBg;
        this.productVideo = productVideo;
        this.containDangerousGoods = containDangerousGoods === "Contains battery / flammables / liquid" ? true : false;
        this.isDeliveryByBuyPass = isDeliveryByBuyPass;
        this.sellerBrandId = sellerBrandId;
        this.variants = variants;
        this.contentScore = contentScore;
        this.productSpecifications = [];
        if (productSpecifications) {
            let keys = Object.keys(productSpecifications);
            for (let key of keys) {
                this.productSpecifications.push(new KeyValueDTO(key, productSpecifications[key]))
            }
        }
        this.description = new Description(
            mainDescription ?? "",
            productDescription ?? "",
            highlights ?? "",
            whatIsInTheBox ?? "",
        );
        this.warranty = new WarrantyDTO(
            warrantyType,
            new UnitDTO(0, warrantyPeriod),
            warrantyPolicy,
        );
        this.package = new PackageDTO(
            isIndividual,
            new UnitDTO(+packageWeight, packageWeightUnit),
            new DimensionDTO(
                +packageLength,
                +packageWidth,
                +packageHeight,
                packageWeightUnit
            )
        );
        this.subProduct = [];
        for (let subproduct of subProducts) {
            let packageDTO = new PackageDTO(
                subproduct.packageWeightUnit ? true : false,
                new UnitDTO(+subproduct.packageWeight, subproduct.packageWeightUnit ? subproduct.packageWeightUnit.label : "kg"),
                new DimensionDTO(
                    +subproduct.packageLength,
                    +subproduct.packageWidth,
                    +subproduct.packageHeight,
                    subproduct.packageWeightUnit ? subproduct.packageWeightUnit.label : "kg"
                ),
            );
            let extraFields: KeyValueDTO[] = [];
            if (subproduct.extra) {
                let extraKeys = Object.keys(subproduct.extra);
                if (extraKeys.length) {
                    for (let key of extraKeys) {
                        if (key === "Special Price") {
                            continue;
                        }
                        extraFields.push(new KeyValueDTO(key, subproduct.extra[key]));
                    }
                }
            }
            let tempSub = new SubProduct(
                packageDTO,
                undefined,
                undefined,
                subproduct.variants,
                +subproduct.Price,
                subproduct.extra["Special Price"] ? subproduct.extra["Special Price"] : null,
                +subproduct.Stock,
                subproduct["Free Items"],
                subproduct["Availablility"] ? true : false,
                extraFields,
                0,
                subproduct.SellerSKU,
                subproduct.sku, 
            );
            tempSub.referenceId = subproduct.referenceId;
            this.subProduct.push(tempSub);
        }
        if (sizeChartImage) {
            this.sizeChart = new SizeChart(sizeChartImage);
        }
        if (draftId) {
            this.draftId = draftId;
        }
    }

    public static parse(formJSON: any, source: AddProductDTO): any {
        const formVariants = formJSON?.find((x: any) => x.name === "customSaleProp");
        for (let variant of source?.variants) {
            if (formVariants?.dataSource && Array.isArray(formVariants?.dataSource)) {
                const dataSourceIndex = formVariants.dataSource.findIndex((x: any) => x.text === variant.type);
                if (dataSourceIndex !== -1) {
                    const variantAttribute = formVariants.attributes[dataSourceIndex];
                    if (variantAttribute && Array.isArray(variantAttribute.dataSource)) {
                        variant.options = variantAttribute.dataSource.map((x: any) => ({
                            label: x.text,
                            value: x.text,
                            isDisabled: variant.values.findIndex((y: any) => y.name === x.text) !== -1,
                        }));
                    } else {
                        variant.options = [];
                    }
                }
                for (let variantValue of variant.values) {
                    if (variantValue.images.length) {
                        variant.allowImages = true;
                        break;
                    }
                }
            }
        }
        let productData: any = {
            nameEn: source?.nameEn,
            nameUr: source?.nameUr,
            categoryId: source?.categoryId,
            draftId: source?.draftId,
            productImages: source?.productImages,
            promotionImage: source?.promotionImage,
            promotionWhiteImageBg: source?.promotionWhiteImageBg,
            productVideo: source?.productVideo,
            variants: source?.variants,
            ["Dangerous Goods"]: source?.containDangerousGoods ? "Contains battery / flammables / liquid" : "None",
        };
        if (source?.description) {
            productData["Main Description"] = source.description.mainDescription;
            productData["Product Description"] = source.description.productDescription;
            productData["Highlights"] = source.description.highlights;
            productData["What's in the box"] = source.description.whatIsInTheBox;
        }
        if (source?.package) {
            productData.packageBySku = source.package.isIndividual;
            if (source.package.dimensions) {
                productData.packageWidth = source.package.dimensions.width;
                productData.packageHeight = source.package.dimensions.height;
                productData.packageLength = source.package.dimensions.length;
            }
            if (source.package.weight) {
                productData.packageWeight = source.package.weight.value;
                productData.packageWeightUnit = source.package.weight.unit
                //  { label: source.package.weight.unit, value: source.package.weight.unit };
            }
        }
        if (source?.warranty) {
            productData["Warranty Type"] = source.warranty.type;
            if (source.warranty.period) {
                productData["Warranty Period"] = source.warranty.period.unit;
            }
            productData["Warranty Policy"] = source.warranty.policy;
        }

        const attributesCategory = formJSON.find((x: any) => x.label === "Product Attributes");
        if (attributesCategory && attributesCategory.dataSource && attributesCategory.dataSource.length) {
            productData["Product Attributes"] = {};
            if (Array.isArray(source?.productSpecifications)) {
                for (const attribute of source.productSpecifications) {
                    const formCategory = attributesCategory.dataSource.find((x: any) => x.label === attribute.key);
                    if (formCategory) {
                        if (formCategory.multiple &&
                            (
                                formCategory.uiType === "checkbox" ||
                                (formCategory.uiType === "combobox" && formCategory.dataSource && formCategory.dataSource.length)
                            )
                        ) {
                            productData["Product Attributes"][attribute.key] = attribute.value.split(',');
                        } else {
                            productData["Product Attributes"][attribute.key] = attribute.value;
                        }
                    }
                }
            }
        }
        if (source?.sizeChart) {
            if (source.sizeChart.type == SizeChartTypes.image) {
                productData.sizeChartImage = source.sizeChart.image;
            }
        }
        if (source?.subProduct && source?.subProduct.length) {
            productData.subProducts = [];
            for (let subproduct of source.subProduct) {
                let tempSubProduct: any = {};
                if (subproduct.productPackage) {
                    if (subproduct.productPackage.dimensions) {
                        tempSubProduct.packageLength = subproduct.productPackage.dimensions.length;
                        tempSubProduct.packageWidth = subproduct.productPackage.dimensions.width;
                        tempSubProduct.packageHeight = subproduct.productPackage.dimensions.height;
                    }
                    if (subproduct.productPackage.weight) {
                        tempSubProduct.packageWeight = subproduct.productPackage.weight.value;
                        tempSubProduct.packageWeightUnit = { label: subproduct.productPackage.weight.unit, value: subproduct.productPackage.weight.unit };
                    }
                }
                tempSubProduct.extra = {};
                for (let extraField of subproduct.extraFields) {
                    tempSubProduct.extra[extraField.key] = extraField.value;
                }
                tempSubProduct.referenceId = subproduct.referenceId;
                tempSubProduct.Price = subproduct.price;
                tempSubProduct.specialPrice = subproduct.specialPrice;
                if (subproduct.specialPrice) {
                    tempSubProduct.extra["Special Price"] = subproduct.specialPrice;
                }
                tempSubProduct.Stock = subproduct.stock;
                tempSubProduct.sku = subproduct.sku;
                tempSubProduct["Free Items"] = subproduct.freeItems;
                tempSubProduct["Availablility"] = subproduct.isAvailable;
                tempSubProduct.SellerSKU = subproduct.sellerSku;
                // tempSubProduct.SellerSKU = subproduct.sku;
                tempSubProduct.variants = subproduct.variants;
                if (subproduct.variants.length === 1) {
                    let subVariant = subproduct.variants[0];
                    tempSubProduct.id = subVariant.value.name;
                    tempSubProduct.secondaryId = subVariant.value.name;
                } else if (subproduct.variants.length > 1) {
                    tempSubProduct.id = subproduct.variants[0].value.name;
                    tempSubProduct.secondaryId = [];
                    for (let i = 0; i < subproduct.variants.length; i++) {
                        tempSubProduct.secondaryId.push(subproduct.variants[i].value.name);
                    }
                    tempSubProduct.secondaryId = tempSubProduct.secondaryId.join('_');
                }
                productData.subProducts.push(tempSubProduct);
            }
        }
        return productData
    }
}
